export type PollTask = (signal: AbortSignal) => Promise<void>

/**
 * Starts an abortable poll whose delay begins after the previous call settles.
 * This deliberately uses setTimeout rather than setInterval: a slow request can
 * never cause a second copy of itself to pile up behind it.
 */
export function startSingleFlightPoll(task: PollTask, intervalMs: number): () => void {
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined

  const run = async () => {
    try {
      await task(controller.signal)
    } catch {
      // Callers own error presentation. A failed poll still schedules the next
      // attempt unless the component has been unmounted.
    } finally {
      if (!controller.signal.aborted) timer = setTimeout(() => void run(), intervalMs)
    }
  }

  void run()

  return () => {
    controller.abort()
    if (timer) clearTimeout(timer)
  }
}
